import { useMemo, useState } from 'react';
import { validateLocation } from '@lp/features/RoutePlanner/utils';
import { CaseStatusMenu } from '@lp/features/CaseOverview/components/Header/CaseStatusMenu';
import { useRoutePlannerData } from '@lp/features/RoutePlanner/contexts/RoutePlannerDataContext';
import { TrialProgramImage } from '@ui';
import { useDeviceLocationTracking } from '@lp/core/hooks/useDeviceLocationTracking';
import { AircraftUtilizationProvider } from '@lp/features/RoutePlanner/contexts/AircraftUtilizationContext';

import { LegLocationTypeEnum } from '@/api/apis/route';
import { LogisticsTimeMetrics } from './LogisticsTimeMetrics';
import { RouteStatusMenu } from './RouteStatusMenu';
import { HospitalsBlock } from './HospitalsBlock';
import { MainRouteSchema } from './MainRouteSchema';
import { RouteBuilderStatuses } from './RouteBuilderStatuses';
import { RouteBanners } from './RouteBanners';
import { LogisticsResources } from './LogisticsResources';
import { CaseTitle } from './CaseTitle';

export const CaseRouteInformation = () => {
  const { caseData, mainRoute, dryRunRoute } = useRoutePlannerData();
  const {
    caseId,
    status,
    routePlanningStatus,
    donorHospitalDetails,
    txCenterDetails,
    donorHospitalDate,
    donorHospitalTime,
    donorHospitalTimeZone,
    recipientHospitalDate,
    recipientHospitalTime,
    recipientHospitalTimeZone,
    txEtaHospitalDate,
    txEtaHospitalTime,
    trialProgram,
    logisticsReference,
  } = caseData;
  const { legs: mainLegs } = mainRoute;
  const { legs: dryRunLegs } = dryRunRoute;
  const deviceTracking = useDeviceLocationTracking({ caseId, status, logisticsId: logisticsReference?.id });
  const [showHospitals, setShowHospitals] = useState(true);

  const handleShowHospitals = () => setShowHospitals((prevState) => !prevState);

  const isSameDH = useMemo(() => {
    const hasSameMainDHLegs = validateLocation(mainLegs, LegLocationTypeEnum.hospital, donorHospitalDetails!.id);
    const hasSameDryRunDHLegs = validateLocation(dryRunLegs, LegLocationTypeEnum.hospital, donorHospitalDetails!.id);

    return hasSameMainDHLegs && hasSameDryRunDHLegs;
  }, [mainLegs, dryRunLegs, donorHospitalDetails]);

  const isSameTX = useMemo(() => {
    if (!txCenterDetails) return true;

    const hasSameMainTXLegs = validateLocation(mainLegs, LegLocationTypeEnum.txcenter, txCenterDetails.id);
    const hasSameDryRunTXLegs = validateLocation(dryRunLegs, LegLocationTypeEnum.txcenter, txCenterDetails.id);

    return hasSameMainTXLegs && hasSameDryRunTXLegs;
  }, [mainLegs, dryRunLegs, txCenterDetails]);

  return (
    <div
      className="w-full p-6 bg-base-80 flex flex-col gap-8"
      style={{
        boxShadow: '0px 4px 9px 0px rgba(201, 202, 209, 0.3)',
      }}
    >
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex flex-col gap-1 items-start w-full">
          <div className="flex items-center w-full">
            <CaseTitle showHospitals={showHospitals} onShowHospitals={handleShowHospitals} />
            {!!routePlanningStatus && <RouteStatusMenu isValidLocations={isSameDH && isSameTX} />}
          </div>
          <div className="ml-8 mb-1 flex gap-2 items-center">
            <TrialProgramImage organ={trialProgram?.organ} />
            <CaseStatusMenu caseId={caseId} />
          </div>
          <AircraftUtilizationProvider>
            <RouteBanners isSameDH={isSameDH} isSameTX={isSameTX} />
          </AircraftUtilizationProvider>
        </div>
        {showHospitals && (
          <HospitalsBlock
            donorHospital={donorHospitalDetails!}
            txCenter={txCenterDetails}
            deviceTracking={deviceTracking}
            donorHospitalDate={donorHospitalDate}
            donorHospitalTime={donorHospitalTime}
            donorHospitalTimeZone={donorHospitalTimeZone}
            recipientHospitalDate={recipientHospitalDate}
            recipientHospitalTime={recipientHospitalTime}
            recipientHospitalTimeZone={recipientHospitalTimeZone}
            txEtaHospitalDate={txEtaHospitalDate}
            txEtaHospitalTime={txEtaHospitalTime}
          />
        )}
      </div>
      <div className="flex flex-col gap-4">
        <div className="flex flex-row justify-between items-center">
          <h1 className="text-hm">Route Details</h1>
          <RouteBuilderStatuses />
        </div>
        <LogisticsTimeMetrics />
        <MainRouteSchema donorHospital={donorHospitalDetails!} txCenter={txCenterDetails} />
        <LogisticsResources />
      </div>
    </div>
  );
};

export default CaseRouteInformation;
